<template>
    <!-- Main container with 100% width/height -->
    <div class="d-flex wrapper">
        <!-- Sidebar container with dynamic classes for compact mode, default mode and mobile view -->
        <div
            :class="[
                'sidebar d-flex flex-column h-100',
                {
                    'sidebar-compact': showAsCompact,
                    'sidebar-closed': !visible,
                    'sidebar-mobile': this.$isMobile,
                },
            ]"
        >
            <!-- Header component with mobile/compact state handling -->
            <SideBarHeader
                :isMobile="this.$isMobile"
                :isCompact="showAsCompact"
                :class="[
                    'sidebar-header',
                    { 'sidebar-header-compact': showAsCompact },
                ]"
                @link-clicked="linkClicked()"
                @toggle-sidebar="toggleSidebar()"
            />
            <!-- Main sidebar content with navigation and footer -->
            <div
                :class="[
                    'sidebar-content',
                    { 'sidebar-content-compact': showAsCompact },
                ]"
            >
                <SideBarNavigation
                    :isCompact="showAsCompact"
                    v-on:link-clicked="linkClicked()"
                />
                <SideBarFooter
                    class="sidebar-footer"
                    :is-compact="showAsCompact"
                    @patty="pattyStore.openNew()"
                    @feedback="showFeedbackForm"
                />
            </div>
        </div>

        <div
            id="app-right-panel"
            class="right-panel h-100"
            :class="{
                'right-panel-absolute': this.$isMobile,
                locked: this.$isMobile && visible,
            }"
        >
            <slot></slot>
        </div>
    </div>

    <div
        v-if="this.$isMobile && visible"
        class="overlay"
        @click="toggleSidebar()"
    ></div>

    <!-- Feedback Form Side Panel -->
    <SidePanel
        :visible="isFeedbackPanelVisible"
        :title="$t('contactUs.feedback.panelTitle')"
        size="panel-md"
        @close="closeFeedbackPanel"
    >
        <FeedbackForm
            :success="feedbackFormSuccess"
            @submit="handleFeedbackSubmission"
        />
    </SidePanel>
</template>

<script>
import SideBarHeader from '@/components/app_side_bar/SideBarHeader.vue';
import SideBarNavigation from '@/components/app_side_bar/SideBarNavigation.vue';
import SideBarFooter from '@/components/app_side_bar/SideBarFooter.vue';
import SidePanel from '@/components/SidePanel.vue';
import FeedbackForm from '@/components/contact_us/feedbackForm.vue';

import { usePattyStore } from '@/stores/patty';
import { useMessengerStore } from '@/stores/messenger';

export default {
    name: 'AppSidebar',

    components: {
        SideBarHeader,
        SideBarNavigation,
        SideBarFooter,
        SidePanel,
        FeedbackForm,
    },

    props: {
        visible: {
            type: Boolean,
            required: true,
        },
    },

    emits: ['close', 'open'],

    data() {
        return {
            pattyStore: null,
            messengerStore: null,
            isFeedbackPanelVisible: false,
            feedbackFormSuccess: false,
        };
    },

    computed: {
        showAsCompact() {
            return !this.visible && !this.$isMobile;
        },
    },
    methods: {
        linkClicked() {
            if (this.$isMobile) this.$emit('close');
        },
        toggleSidebar() {
            if (this.visible) this.$emit('close');
            else this.$emit('open');
        },
        showFeedbackForm() {
            this.isFeedbackPanelVisible = true;
        },
        closeFeedbackPanel() {
            this.isFeedbackPanelVisible = false;
            this.feedbackFormSuccess = false;
        },
        handleFeedbackSubmission(formData) {
            this.$postData('user-feedback/create', formData).then(
                (response) => {
                    if (response.success) {
                        this.$alertStore.pushSuccess(
                            this.$t('contactUs.feedback.success')
                        );
                        this.feedbackFormSuccess = true;
                    } else if (response.message) {
                        this.$alertStore.pushError(response.message);
                    }
                }
            );
        },
    },

    created() {
        this.pattyStore = usePattyStore();
        this.messengerStore = useMessengerStore();
    },
};
</script>

<style scoped>
/* Main sidebar container */
.sidebar {
    padding-top: var(--space-4);
    padding-bottom: var(--space-4);
    width: 17.5rem;
    background-color: var(--white);
    transition: width 0.3s ease-in-out, padding 0.3s ease-in-out;
    overflow: hidden;
}

/* Compact mode styles */
.sidebar-compact {
    padding-bottom: var(--space-2);
    width: 6.75rem;
}

/* Header section styling */
.sidebar-header {
    position: sticky;
    background: var(--white);
    z-index: 1;
    margin-bottom: var(--space-4);
    padding-left: var(--space-5);
    padding-right: var(--space-5);
}

.sidebar-header-compact {
    padding-left: var(--space-2);
    padding-right: var(--space-2);
}

/* Footer section spacing */
.sidebar-footer {
    margin-top: var(--space-9);
    gap: var(--space-3);
}

/* Scrollable content area */
.sidebar-content {
    overflow-y: auto;
    padding-right: var(--space-6);
    padding-left: var(--space-6);
    transition: all 0.3s ease-in-out;
}

/* Compact content padding */
.sidebar-content-compact {
    padding-right: var(--space-2);
    padding-left: var(--space-2);
}

/* Mobile responsive adjustments */
@media only screen and (max-width: 767.98px) {
    .sidebar {
        width: 240px;
        transition: all 0.5s cubic-bezier(0.36, -0.01, 0, 0.77);
    }
    .sidebar-closed {
        margin-left: -240px;
    }

    .sidebar-header {
        padding-left: var(--space-4);
        padding-right: var(--space-4);
        margin-bottom: var(--space-4);
    }

    .sidebar-footer {
        margin-top: 24px;
    }

    /* Overlay to cover the rest of the page */
    .overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        /* semi-transparent overlay */
        background-color: rgba(64, 89, 81, 0.25);
        /* one level below the panel */
        z-index: 1049;
    }
}

/* Main content panel */
.right-panel {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 0;
    min-width: 0;
    overflow: hidden;
    transition: all 0.3s ease;
}

.right-panel-absolute {
    flex-shrink: 0;
    width: 100%;
}

.locked {
    pointer-events: none;
}

.sidebar-mobile {
    position: absolute;
    z-index: 1050;
}

.wrapper {
    height: 100%;
    overflow: hidden;
}
</style>
